import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'

import { api, can, isActive, type RunLogEntry } from '../api'
import { Pill, RunStatus } from '../components'
import { bytes, dt, elapsed, num, progressText, statusKind } from '../format'
import { pollWhile, useNow } from '../hooks'
import { SnapshotsTable, TransfersTable } from './zoneTables'

const time = new Intl.DateTimeFormat('sv-SE', { timeStyle: 'medium', timeZone: 'Europe/Stockholm' })

function fields(e: RunLogEntry): string {
  const { at: _at, level: _level, msg: _msg, ...rest } = e
  return Object.entries(rest)
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' ')
}

export function RunDetailPage() {
  const id = Number(useParams().id)
  const qc = useQueryClient()
  const now = useNow(1000)
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const q = useQuery({
    queryKey: ['run', id],
    queryFn: () => api.run(id),
    refetchInterval: (query) => pollWhile(isActive(query.state.data), 1_500, 30_000),
  })
  const cancel = useMutation({
    mutationFn: () => api.cancelRun(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['run', id] }),
  })

  if (q.error) return <p className="callout err">{q.error.message}</p>
  if (!q.data) return <p className="muted">Loading…</p>
  const r = q.data
  const targetLink =
    r.target_kind === 'source' ? `/sources/${r.target_slug}` : r.target_kind === 'bucket' ? `/buckets/${r.target_slug}` : undefined

  return (
    <section className="page">
      <p className="crumbs">
        <Link to="/runs">Runs</Link> / <span className="mono">#{r.id}</span>
      </p>
      <header className="page-h">
        <div>
          <h1>
            Run #{r.id} <span className="muted">{r.kind}</span>
          </h1>
          <p className="sub">
            {targetLink ? <Link to={targetLink}>{r.target_slug}</Link> : <span className="mono">{r.target_slug}</span>} ·{' '}
            {r.trigger} by {r.created_by} · {dt(r.created_at)}
          </p>
        </div>
        <div className="row">
          <RunStatus run={r} link={false} />
          {can(me.data, 'editor') && isActive(r) && (
            <button className="btn" disabled={cancel.isPending || r.cancel_requested} onClick={() => cancel.mutate()}>
              {r.cancel_requested ? 'Cancelling…' : 'Cancel run'}
            </button>
          )}
        </div>
      </header>
      {cancel.error && <p className="callout err">{cancel.error.message}</p>}

      {r.status === 'running' && (
        <div className="progress" role="status">
          <span className="dot" aria-hidden />
          <span>{progressText(r.progress) || 'starting'}</span>
          <span className="muted">{elapsed(r.started_at, null, now)}</span>
        </div>
      )}
      {r.status === 'queued' && <p className="callout warn">Waiting for the worker. Runs execute one at a time.</p>}
      {r.error && <p className="callout err">{r.error}</p>}
      {r.fingerprint_diff && (
        <div className="card">
          <h2>Layout change</h2>
          <p className="note">The file no longer matches the template; nothing was written. Fix the template in the extraction editor and re-extract.</p>
          {Object.entries(r.fingerprint_diff).map(([tpl, d]) => (
            <div key={tpl}>
              <p>
                <Link to={`/extraction/${r.target_slug}?template=${tpl}`} className="mono">{tpl}</Link>
                {d.missing?.length ? <> · missing columns: <strong>{d.missing.join(', ')}</strong></> : ' · header layout changed'}
              </p>
              <p className="note mono">headers now: {d.headers.join(' | ')}</p>
            </div>
          ))}
        </div>
      )}

      <div className="cols-2">
        <div className="card">
          <h2>Summary</h2>
          <dl className="kv">
            <dt>Status</dt>
            <dd><Pill kind={statusKind(r.status)}>{r.status}</Pill></dd>
            <dt>Started</dt>
            <dd>{dt(r.started_at)}</dd>
            <dt>Finished</dt>
            <dd>{dt(r.finished_at)}</dd>
            <dt>Duration</dt>
            <dd>{elapsed(r.started_at, r.finished_at, now)}</dd>
            <dt>Records in</dt>
            <dd className="mono">{num(r.rows_in)}</dd>
            <dt>Rows loaded</dt>
            <dd className="mono">{num(r.rows_out)}</dd>
            <dt>Input versions</dt>
            <dd><code className="block">{JSON.stringify(r.input_versions)}</code></dd>
            <dt>Output</dt>
            <dd className="mono">{r.output_version ?? '–'}</dd>
            {r.kind === 'build' && (
              <>
                <dt>Quarantined</dt>
                <dd className="mono">{num(r.quarantined)}</dd>
                <dt>Schema</dt>
                <dd className="mono">
                  {r.schema_fingerprint?.slice(0, 12) ?? '–'}
                  {r.prev_fingerprint && r.schema_fingerprint && r.prev_fingerprint !== r.schema_fingerprint && (
                    <> <Pill kind="warn">changed since the previous build</Pill></>
                  )}
                </dd>
              </>
            )}
            <dt>Worker</dt>
            <dd className="mono">{r.worker ?? '–'}</dd>
          </dl>
        </div>
        <div className="card">
          <h2>Log</h2>
          {r.log.length === 0 ? (
            <p className="muted">Nothing logged yet.</p>
          ) : (
            <ol className="runlog">
              {r.log.map((e, i) => (
                <li key={i} className={`lvl-${e.level}`}>
                  <span className="muted">{time.format(new Date(e.at))}</span>
                  <span>{e.level}</span>
                  <span>
                    {e.msg} <span className="fields">{fields(e)}</span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {r.build && (
        <div className="card">
          <h2>Output</h2>
          <dl className="kv">
            <dt>Rows</dt>
            <dd className="mono">{num(r.build.rows)}</dd>
            <dt>Data from</dt>
            <dd>{dt(r.build.data_fetched_at)}</dd>
            <dt>Parquet</dt>
            <dd className="mono">
              {r.build.output_key} <span className="muted">{r.build.output_deleted_at ? '(deleted by retention)' : bytes(r.build.bytes)}</span>
            </dd>
            <dt>Postgres</dt>
            <dd className="mono">{r.build.serve_table ?? '–'}</dd>
            <dt>Licence</dt>
            <dd>
              {r.build.effective_license.licenses.join(' + ')} <span className="muted mono">{r.build.effective_license.usage_mode}</span>
            </dd>
          </dl>
        </div>
      )}
      {r.quarantine_rules.length > 0 && (
        <div className="card">
          <h2>Quarantine</h2>
          <table className="tbl">
            <thead>
              <tr><th>Rule</th><th>Reason</th><th className="num">Rows</th></tr>
            </thead>
            <tbody>
              {r.quarantine_rules.map((q) => (
                <tr key={q.rule}>
                  <td className="mono">{q.rule}</td>
                  <td>{q.reason}</td>
                  <td className="num">{num(q.rows)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="note">First {r.quarantine.length} rows (all of them are in the run's quarantine Parquet):</p>
          <table className="tbl">
            <thead>
              <tr><th>Rule</th><th>Row</th></tr>
            </thead>
            <tbody>
              {r.quarantine.map((q, i) => (
                <tr key={i}>
                  <td className="mono">{q.rule}</td>
                  <td><code>{JSON.stringify(q.row)}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {r.transfers.length > 0 && (
        <div className="card">
          <h2>Zone checks</h2>
          <TransfersTable rows={r.transfers} />
        </div>
      )}
      {r.snapshots.length > 0 && (
        <div className="card">
          <h2>Snapshots</h2>
          <SnapshotsTable rows={r.snapshots} />
        </div>
      )}
    </section>
  )
}
